
import { useState } from "react"
import styled from "styled-components"
import { Plus, X, Edit2, Trash2 } from "lucide-react"
import { motion, AnimatePresence } from "framer-motion"
import toast from "react-hot-toast"
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

const SizesSection = styled(motion.div)`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 20px;
  background-color: #2A2B2E;
  padding: 20px;
  border-radius: 8px;
`

const SizesHeader = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 10px;
  align-items: center;
`

const Title = styled.h2`
  margin: 0;
  font-size: 1rem;
  color: #FFFFFF;
`

const Button = styled.button`
  padding: 8px 16px;
  border-radius: 4px;
  border: none;
  background-color: #4A88F3;
  color: white;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  transition: background-color 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background-color: #3B78E0;
  }
`

const SizesList = styled.ul`
  list-style-type: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const SizeItem = styled(motion.li)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #3A3B3E;
  padding: 10px;
  border-radius: 4px;
`

const SizeInfo = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`

const SizeActions = styled.div`
  display: flex;
  gap: 10px;
`

const ActionButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #FFFFFF;
  transition: color 0.3s ease;

  &:hover {
    color: #4A88F3;
  }
`

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`

const ModalContent = styled(motion.div)`
  background-color: #2A2B2E;
  padding: 20px;
  border-radius: 8px;
  width: 90%;
  max-width: 400px;
`

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`

const ModalTitle = styled.h2`
  color: #FFFFFF;
  margin: 0;
`

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #FFFFFF;
  cursor: pointer;
  font-size: 1.5rem;
`

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const Label = styled.label`
  color: #FFFFFF;
  font-size: 0.9rem;
`

const Input = styled.input`
  padding: 10px;
  border-radius: 4px;
  border: 1px solid #4A4B4F;
  background-color: #3A3B3E;
  color: white;
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: #4A88F3;
  }
`



export const ProductSizes = ({ sizes, onAddSize, onEditSize, onDeleteSize, create }) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingSize, setEditingSize] = useState(null)
  const [newSize, setNewSize] = useState({ id: "", name: "" })

  const handleOpenModal = (size) => {
    if (create) {
      return toast("Debe crearse el producto antes de añadir tallas", {
        icon: <WarningAmberIcon />
      })
    }

    if (size) {
      setEditingSize(size)
      setNewSize(size)
    } else {
      setEditingSize(null)
      setNewSize({ id: "", name: "" })
    }
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setEditingSize(null)
    setNewSize({ id: "", name: ""})
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (editingSize) {
      onEditSize(editingSize.id, newSize)
    } else {
      onAddSize({ ...newSize, id: Date.now().toString() })
    }
    handleCloseModal()
  }

  return (
    <SizesSection initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
      <SizesHeader>
        <Title>Tallas disponibles</Title>
        <Button onClick={() => handleOpenModal()}>
          <Plus size={16} />
          Añadir talla
        </Button>
      </SizesHeader>
      <SizesList>
        <AnimatePresence>
          {sizes?.map((size) => (
            <SizeItem
              key={size.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.3 }}
            >
              <SizeInfo>
                <span>{size.name}</span>
              </SizeInfo>
              <SizeActions>
                <ActionButton onClick={() => handleOpenModal(size)} title="Editar">
                  <Edit2 size={16} />
                </ActionButton>
                <ActionButton onClick={() => onDeleteSize(size.id)} title="Eliminar">
                  <Trash2 size={16} />
                </ActionButton>
              </SizeActions>
            </SizeItem>
          ))}
        </AnimatePresence>
      </SizesList>

      <AnimatePresence>
        {isModalOpen && (
          <ModalOverlay initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <ModalContent
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
            >
              <ModalHeader>
                <ModalTitle>{editingSize ? "Editar talla" : "Añadir talla"}</ModalTitle>
                <CloseButton onClick={handleCloseModal}>
                  <X size={24} />
                </CloseButton>
              </ModalHeader>
              <Form onSubmit={handleSubmit}>
                <FormGroup>
                  <Label htmlFor="sizeName">Nombre de la talla</Label>
                  <Input
                    id="sizeName"
                    type="text"
                    value={newSize.name}
                    onChange={(e) => setNewSize({ ...newSize, name: e.target.value })}
                    required
                  />
                </FormGroup>
                <Button type="submit">{editingSize ? "Guardar cambios" : "Añadir talla"}</Button>
              </Form>
            </ModalContent>
          </ModalOverlay>
        )}
      </AnimatePresence>
    </SizesSection>
  )
}

